<aside class="flex flex-col h-full w-full bg-nav overflow-hidden transition-all duration-200 shadow-[4px_0_24px_rgba(0,0,0,0.18)]">

  <!-- Logo / company block -->
  <div class="group flex items-center h-14 border-b border-nav-text/10 shrink-0 transition-all duration-200"
       [class.px-4]="!collapsed"
       [class.px-[14px]]="collapsed"
       [class.justify-between]="!collapsed"
       [class.justify-center]="collapsed">
    
    @if (!collapsed) {
      <!-- Uncollapsed Sidebar Header: Logo, Company info, and Collapse button -->
      <div class="flex items-center min-w-0 flex-1">
        <div class="w-7 h-7 rounded-lg overflow-hidden shrink-0 shadow-sm ring-1 ring-white/10">
          <img src="workwell-logo.jpg" alt="WorkWell" class="w-full h-full object-cover" />
        </div>
        <div class="relative min-w-0 flex-1 ml-3 transition-opacity duration-200" (click)="$event.stopPropagation()">
          @if (tenants.length > 1 || (isSuperAdmin && companies.length > 0)) {
            <button
              type="button"
              class="organisation-trigger"
              [class.organisation-trigger--interactive]="canSwitchOrganisation"
              [disabled]="!canSwitchOrganisation || switchingOrganisation"
              [attr.aria-expanded]="organisationMenuOpen"
              aria-haspopup="listbox"
              aria-label="Switch organisation"
              (click)="toggleOrganisationMenu($event)"
            >
              <span class="min-w-0 text-left">
                <span class="organisation-name">{{ selectedCompanyName }}</span>
                <span class="organisation-caption">Organisation</span>
              </span>
              @if (switchingOrganisation) {
                <i class="pi pi-spin pi-spinner organisation-chevron" aria-hidden="true"></i>
              } @else if (canSwitchOrganisation) {
                <i class="pi pi-chevron-down organisation-chevron" [class.rotate-180]="organisationMenuOpen" aria-hidden="true"></i>
              }
            </button>

            @if (organisationMenuOpen) {
              <div class="organisation-menu" role="listbox" aria-label="Available organisations">
                <p class="organisation-menu-label">Switch organisation</p>
                @if (tenants.length > 1) {
                  @for (tenant of tenants; track tenantId(tenant)) {
                    <button
                      type="button"
                      role="option"
                      class="organisation-option"
                      [class.organisation-option--active]="tenantId(tenant) === auth.session()?.user?.tenantId"
                      [attr.aria-selected]="tenantId(tenant) === auth.session()?.user?.tenantId"
                      (click)="selectTenant(tenant)"
                    >
                      <span class="organisation-mark">{{ tenantName(tenant).charAt(0).toUpperCase() }}</span>
                      <span class="truncate flex-1">{{ tenantName(tenant) }}</span>
                      @if (tenantId(tenant) === auth.session()?.user?.tenantId) {
                        <i class="pi pi-check" aria-hidden="true"></i>
                      }
                    </button>
                  }
                } @else {
                  @for (company of companies; track company.compId) {
                  <button
                    type="button"
                    role="option"
                    class="organisation-option"
                    [class.organisation-option--active]="company.compId === selectedCompanyId"
                    [attr.aria-selected]="company.compId === selectedCompanyId"
                    (click)="selectCompany(company.compId)"
                  >
                    <span class="organisation-mark">{{ company.name.charAt(0).toUpperCase() }}</span>
                    <span class="truncate flex-1">{{ company.name }}</span>
                    @if (company.compId === selectedCompanyId) {
                      <i class="pi pi-check" aria-hidden="true"></i>
                    }
                  </button>
                  }
                }
              </div>
            }
          } @else {
            <p class="text-[13px] font-semibold text-nav-on-text truncate">{{ companyName }}</p>
            @if (companySubtitle) {
              <p class="text-[10px] text-nav-text truncate">{{ companySubtitle }}</p>
            }
          }
        </div>
      </div>
      
      <!-- Collapse toggle button -->
      <button
        type="button"
        (click)="sidebarService.toggle()"
        class="flex items-center justify-center w-7 h-7 rounded-md text-nav-text hover:text-white hover:bg-white/10 transition-colors cursor-pointer bg-transparent border-0 p-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
        aria-label="Collapse sidebar"
        title="Collapse sidebar"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect width="18" height="18" x="3" y="3" rx="2" />
          <path d="M9 3v18" />
        </svg>
      </button>
    } @else {
      <!-- Collapsed Sidebar Header: Logo replaced by Expand button on hover -->
      <div class="relative w-7 h-7 flex items-center justify-center shrink-0">
        <!-- Logo (visible by default, fades out on hover) -->
        <div class="group-hover:opacity-0 group-hover:pointer-events-none transition-all duration-150 absolute inset-0 rounded-lg overflow-hidden shadow-sm ring-1 ring-white/10">
          <img src="workwell-logo.jpg" alt="WorkWell" class="w-full h-full object-cover" />
        </div>
        
        <!-- Expand button (fades in on hover) -->
        <button
          type="button"
          (click)="sidebarService.toggle()"
          class="opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-all duration-150 absolute inset-0 flex items-center justify-center w-7 h-7 rounded-md text-nav-text hover:text-white hover:bg-white/10 cursor-pointer bg-transparent border-0 p-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
          title="Expand sidebar"
          aria-label="Expand sidebar"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="rotate-180">
            <rect width="18" height="18" x="3" y="3" rx="2" />
            <path d="M9 3v18" />
          </svg>
        </button>
      </div>
    }
  </div>

  <!-- Nav items — config-driven -->
  <nav class="flex-1 py-3 mt-5 overflow-y-auto overflow-x-hidden space-y-0.5">
    @if (navItems.length > 0) {
      @for (item of navItems; track item.route) {
        <a
          [routerLink]="item.route"
          routerLinkActive="active-nav-item"
          [routerLinkActiveOptions]="{ exact: item.route === '/dashboard' }"
          class="relative flex items-center rounded-lg text-nav-text hover:bg-white/[0.06] hover:text-nav-on-text hover:translate-x-0.5 transition-all duration-150 ease-out no-underline text-[13px] font-medium overflow-hidden mx-2 py-2.5"
          [class.px-3]="!collapsed"
          [class.justify-center]="collapsed"
          [pTooltip]="collapsed ? item.label : ''"
          tooltipPosition="right"
        >
          <!-- Active left accent bar -->
          <span class="active-accent absolute left-0 top-2 bottom-2 w-[3px] rounded-r-full bg-nav-on opacity-0 transition-opacity duration-150"></span>
          @if (item.icon === 'timesheet-document-clock') {
            <svg class="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M6 2.75h7l5 5v5.5" />
              <path d="M13 2.75v5h5" />
              <path d="M12 21.25H6a2 2 0 0 1-2-2V4.75a2 2 0 0 1 2-2" />
              <path d="M7.5 11h5M7.5 14.5h3.5" />
              <circle cx="16.5" cy="17" r="4.25" />
              <path d="M16.5 14.75V17l1.65 1" />
            </svg>
          } @else {
            <i class="pi shrink-0 text-sm" [ngClass]="item.icon"></i>
          }
          @if (!collapsed) {
            <span class="truncate ml-3 transition-opacity duration-200">{{ item.label }}</span>
          }
        </a>
      }
    } @else {
      <!-- Skeleton nav -->
      <div class="px-2 space-y-1 animate-pulse">
        @for (i of [1,2,3,4,5,6,7]; track i) {
          <div class="flex items-center gap-3 px-3 py-2.5 rounded-lg">
            <div class="w-4 h-4 bg-nav-text/15 rounded shrink-0"></div>
            @if (!collapsed) {
              <div class="h-3 bg-nav-text/15 rounded flex-1"></div>
            }
          </div>
        }
      </div>
    }
  </nav>

  <!-- User identity — above sign out -->
  <!-- <div class="mx-2 mb-1 pt-2 border-t border-nav-text/10 shrink-0 transition-all duration-200">
    <a
      routerLink="/profile"
      routerLinkActive="active-nav-item"
      class="flex items-center py-2 rounded-lg no-underline cursor-pointer hover:bg-white/[0.06] transition-colors duration-150"
      [class.px-2]="!collapsed"
      [class.justify-center]="collapsed"
      [pTooltip]="collapsed ? (userName || 'User') + ' — View profile' : ''"
      tooltipPosition="right"
      title="View profile"
    > -->
      <!-- Avatar -->
      <!-- <div class="w-7 h-7 rounded-full bg-nav-text/10 border border-nav-text/20 flex items-center justify-center shrink-0">
        @if (userInitials) {
          <span class="text-[10px] font-semibold text-nav-on-text leading-none tracking-wide">{{ userInitials }}</span>
        } @else {
          <i class="pi pi-user text-nav-text text-[10px]"></i>
        }
      </div>
      @if (!collapsed) {
        <div class="min-w-0 flex-1 ml-2.5 transition-opacity duration-200">
          <p class="text-[12px] font-medium text-nav-on-text truncate leading-snug">{{ userName || 'User' }}</p>
        </div>
        <i class="pi pi-angle-right text-[10px] text-nav-text shrink-0"></i>
      }
    </a>
  </div> -->

  <!-- Sign out -->
  <div class=" py-1 shrink-0 px-2 border-t border-nav-text/10 shrink-0 transition-all duration-200">

    <button
      type="button"
      (click)="logout.emit()"
      class="flex items-center w-full py-2 rounded-lg text-muted hover:text-negative hover:bg-neg-bg transition-colors duration-150 text-[12px] font-medium border-0 bg-transparent text-left cursor-pointer"
      [class.px-3]="!collapsed"
      [class.justify-center]="collapsed"
      [pTooltip]="collapsed ? 'Sign out' : ''"
      tooltipPosition="right"
    >
      <i class="pi pi-sign-out shrink-0 text-[12px]"></i>
      @if (!collapsed) {
        <span class="ml-2.5 transition-opacity duration-200">Sign out</span>
      }
    </button>
  </div>

  <!-- Version -->
  @if (!collapsed) {
    <div class="px-4 pb-3 shrink-0 transition-opacity duration-200">
      @if (version) {
        <p class="text-[9px] text-nav-text/50 uppercase tracking-widest">{{ version }}</p>
      }
    </div>
  }

</aside>
